import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { AppDispatch } from '../store';
import {
  selectAuth,
  selectUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectAuthError,
  login as loginAction,
  register as registerAction,
  logout as logoutAction,
  getCurrentUser as getCurrentUserAction,
  changePassword as changePasswordAction,
  updateProfile as updateProfileAction,
  clearError,
} from '../store/authSlice';
import {
  LoginCredentials,
  RegisterData,
  ChangePasswordData,
  UpdateProfileData,
} from '../services/auth.service';

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      return dispatch(loginAction(credentials)).unwrap();
    },
    [dispatch]
  );

  const register = useCallback(
    async (data: RegisterData) => {
      return dispatch(registerAction(data)).unwrap();
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    return dispatch(logoutAction()).unwrap();
  }, [dispatch]);

  const getCurrentUser = useCallback(async () => {
    return dispatch(getCurrentUserAction()).unwrap();
  }, [dispatch]);

  const changePassword = useCallback(
    async (data: ChangePasswordData) => {
      return dispatch(changePasswordAction(data)).unwrap();
    },
    [dispatch]
  );

  const updateProfile = useCallback(
    async (data: UpdateProfileData) => {
      return dispatch(updateProfileAction(data)).unwrap();
    },
    [dispatch]
  );

  const clearAuthError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const hasRole = useCallback(
    (role: string) => {
      return user?.roles?.includes(role) || false;
    },
    [user]
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      return user?.roles?.some((role) => roles.includes(role)) || false;
    },
    [user]
  );

  const hasAllRoles = useCallback(
    (roles: string[]) => {
      return roles.every((role) => user?.roles?.includes(role)) || false;
    },
    [user]
  );

  const canAccessCompany = useCallback(
    (companyId: string) => {
      if (!user) return false;

      // SUPER_ADMIN and ADMIN have global access
      if (hasAnyRole(['SUPER_ADMIN', 'ADMIN'])) {
        return true;
      }

      // Check allowed companies
      if (user.allowedCompanyIds.length > 0) {
        return user.allowedCompanyIds.includes(companyId);
      }

      return true; // No restrictions
    },
    [user, hasAnyRole]
  );

  const canAccessJurisdiction = useCallback(
    (jurisdiction: string) => {
      if (!user) return false;

      // SUPER_ADMIN and ADMIN have global access
      if (hasAnyRole(['SUPER_ADMIN', 'ADMIN'])) {
        return true;
      }

      // Check allowed jurisdictions
      if (user.allowedJurisdictions.length > 0) {
        return user.allowedJurisdictions.includes(jurisdiction);
      }

      return true; // No restrictions
    },
    [user, hasAnyRole]
  );

  // Fine-grained permission checks
  const hasPermission = useCallback(
    (permission: string) => {
      if (!user) return false;

      // SUPER_ADMIN bypasses all permission checks
      if (hasRole('SUPER_ADMIN')) {
        return true;
      }

      // Check if user has the specific permission
      return user.permissions?.includes(permission) || false;
    },
    [user, hasRole]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) => {
      if (!user) return false;

      // SUPER_ADMIN bypasses all permission checks
      if (hasRole('SUPER_ADMIN')) {
        return true;
      }

      // Check if user has any of the specified permissions
      return permissions.some((permission) => user.permissions?.includes(permission)) || false;
    },
    [user, hasRole]
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) => {
      if (!user) return false;

      // SUPER_ADMIN bypasses all permission checks
      if (hasRole('SUPER_ADMIN')) {
        return true;
      }

      // Check if user has all of the specified permissions
      return permissions.every((permission) => user.permissions?.includes(permission)) || false;
    },
    [user, hasRole]
  );

  const getPermissions = useCallback(() => {
    return user?.permissions || [];
  }, [user]);

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    auth,

    // Actions
    login,
    register,
    logout,
    getCurrentUser,
    changePassword,
    updateProfile,
    clearAuthError,

    // Role checks
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canAccessCompany,
    canAccessJurisdiction,

    // Permission checks (fine-grained)
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissions,
  };
};

// Convenience hook for permission checks
export const usePermissions = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, getPermissions, user } = useAuth();
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissions,
    permissions: user?.permissions || [],
  };
};
